import { type Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { fetchMarket } from '../accounts/fetch.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID, TOKEN_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveOutcomeMintPda } from '../pda/derivations.js';
import type { BuildResult } from '../types/build-result.js';
import type { BuyParams, SellParams, SwapParams, TradeAccounts } from '../types/trading.js';
import { createBuildResult, createIdempotentAtaIx, uniqueSignerKeys } from './common.js';
import { buildInitPositionIfNeeded } from './sell-cap.js';
import { buildTradeInstruction, tradePda } from './trade-instruction.js';

/** Build a current-contract buy; indexed tradeAccounts skip all account reads. */
export async function buildBuy(connection: Connection, params: BuyParams): Promise<BuildResult> {
  return buildTrade(connection, 'buy', params, params.amountIn, params.minTokensOut, true);
}

/** Build a current-contract sell; indexed tradeAccounts skip all account reads. */
export async function buildSell(connection: Connection, params: SellParams): Promise<BuildResult> {
  return buildTrade(
    connection,
    'sell',
    params,
    params.tokensIn,
    params.minUsdcOut,
    params.ensureOutcomeAtas ?? false,
  );
}

/** Compose canonical sell and buy legs atomically, with ATA creation before either leg. */
export async function buildSwap(connection: Connection, params: SwapParams): Promise<BuildResult> {
  // The swap-wide budget must not reach the legs: each leg budgets itself,
  // including the buy leg's init_position on a capped market.
  const { computeUnits, ...legParams } = params;
  const sell = await buildSell(connection, {
    ...legParams,
    outcomeIndex: params.fromOutcomeIndex,
    tokensIn: params.fromTokensIn,
  });
  const buy = await buildBuy(connection, {
    ...legParams,
    outcomeIndex: params.toOutcomeIndex,
    amountIn: params.buyAmountIn,
  });
  // Both builders prepend compute-budget instructions; the buy already ensures the shared quote ATA.
  return createBuildResult(
    [
      ...buy.instructions.slice(1, -1),
      ...sell.instructions.slice(-1),
      ...buy.instructions.slice(-1),
    ],
    buy.signerKeys,
    computeUnits ?? sell.computeUnits + buy.computeUnits,
  );
}

async function buildTrade(
  connection: Connection,
  side: 'buy' | 'sell',
  params: BuyParams | SellParams,
  amount: BN,
  minimum: BN,
  ensureOutcomeAtas: boolean,
): Promise<BuildResult> {
  validateOutcomeIndex(params.outcomeIndex);
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const accounts = params.tradeAccounts ?? (await loadTradeAccounts(connection, params, programId));
  if (params.collateralMint && !params.collateralMint.equals(accounts.quoteMint)) {
    throw new Error('collateralMint does not match market quote mint');
  }
  const ataPayer = params.payer ?? params.user;
  const preInstructions = [
    createIdempotentAtaIx(ataPayer, params.user, accounts.quoteMint, TOKEN_PROGRAM_ID),
  ];
  if (ensureOutcomeAtas) {
    const claimRightsMint = tradePda(
      programId,
      'claim_rights_mint',
      params.market.toBuffer(),
      Buffer.from([params.outcomeIndex]),
    );
    const [outcomeMint] = deriveOutcomeMintPda(params.market, params.outcomeIndex, programId);
    preInstructions.push(
      createIdempotentAtaIx(ataPayer, params.user, outcomeMint, TOKEN_PROGRAM_ID),
      createIdempotentAtaIx(ataPayer, params.user, claimRightsMint, TOKEN_PROGRAM_ID),
    );
  }
  const initPosition =
    side === 'buy'
      ? await buildInitPositionIfNeeded(connection, {
          market: params.market,
          outcomeIndex: params.outcomeIndex,
          owner: params.user,
          payer: ataPayer,
          programId,
        })
      : null;
  if (initPosition !== null) preInstructions.push(initPosition);
  const ix = await buildTradeInstruction(program, side, params, accounts, amount, minimum);
  return createBuildResult(
    [...preInstructions, ix],
    uniqueSignerKeys(params.user, ataPayer),
    params.computeUnits ??
      estimateComputeUnits(side, 'default') +
        (initPosition === null ? 0 : estimateComputeUnits('init_position')),
  );
}

async function loadTradeAccounts(
  connection: Connection,
  params: BuyParams | SellParams,
  programId: PublicKey,
): Promise<TradeAccounts> {
  const market = await fetchMarket(connection, params.market, programId);
  if (params.outcomeIndex >= market.nOutcomes) {
    throw new Error(
      `outcomeIndex ${params.outcomeIndex} outside market outcome count ${market.nOutcomes}`,
    );
  }
  return {
    marketConfig: market.config,
    quoteMint: market.quoteMint,
    ...(market.eventSchedule.equals(PublicKey.default)
      ? {}
      : { eventSchedule: market.eventSchedule }),
  };
}

function validateOutcomeIndex(outcomeIndex: number): void {
  if (!Number.isInteger(outcomeIndex) || outcomeIndex < 0 || outcomeIndex > 255) {
    throw new Error(`outcome index ${outcomeIndex} outside u8 range`);
  }
}
